/**
 * Individual Disability Benefit Definitions
 *
 * Defines the structure and cell specifications for Individual Disability Insurance benefits.
 * Uses builder functions for standard type/amount patterns.
 */

import {
  addNotDisclosed,
  BaseBenefitPlanPolicyPeriod,
  BenefitDurationTypeEnum,
  DefinitionOfDisabilityEnum,
  EmployerContributionEnum,
  extendEnumWithNotDisclosed,
  NotDisclosed,
  RateGuaranteeTypeEnum,
  RfpCatastrophicBenefitEnum,
  RfpIdiPlanDesignTypeEnum,
  RfpInclusionStatusEnum,
  RfpLtdPlanDesignTypeEnum,
  RfpRecoveryBenefitEnum,
} from "@platform/openapi-types";

import { mpOnlyRow } from "../shared/definitions/mpOnlyRow";
import { BenefitDefinitions } from "../../benefits/benefit.types";
import {
  createEnumFormatter,
  createValueFormatter,
} from "../../components/PlanEditor/utilities/enumFormatters";
import * as sharedCellSpecs from "../shared/cellSpecs/common";
import {
  buildCommissionBenefits,
  buildPreExistingConditionBenefitPair,
  buildTypeAmountBenefitPair,
} from "../shared/definitions/builders";
import {
  benefitDurationLabels,
  definitionOfDisabilityLabels,
  employerContributionLabels,
} from "../shared/definitions/customLabels";

// Pre-build commission definitions to override isRequired on type fields
const commissionDefs = buildCommissionBenefits();

export const benefits: BenefitDefinitions = {
  currentCarrierName: {
    label: "Current Carrier",
    description: "Name of the current insurance carrier",
    cell: sharedCellSpecs.currentCarrierName,
    isRequired: true,
    networkDependent: false,
    benefitPlanLevel: true,
    editableWhen: {
      dependsOn: "policyPeriod",
      disabledValues: [
        BaseBenefitPlanPolicyPeriod.newBusiness,
        BaseBenefitPlanPolicyPeriod.alternate,
      ],
    },
  },
  ...commissionDefs,
  currentCommissionType: {
    ...commissionDefs["currentCommissionType"],
    isRequired: true,
    editableWhen: {
      dependsOn: "policyPeriod",
      disabledValues: [
        BaseBenefitPlanPolicyPeriod.newBusiness,
        BaseBenefitPlanPolicyPeriod.alternate,
      ],
    },
  },
  requestedCommissionType: {
    ...commissionDefs["requestedCommissionType"],
    isRequired: true,
  },
  eligibilityClasses: {
    label: "Eligibility Class",
    description: "Benefit classes assigned to this benefit plan group",
    cell: sharedCellSpecs.eligibilityClasses,
    benefitPlanLevel: true,
    isRequired: true,
  },
  ltdPlanDesignType: {
    label: "LTD Plan Design",
    description: "Type of LTD plan design",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosed(RfpLtdPlanDesignTypeEnum),
      },
    },
    networkDependent: false,
    refreshesCells: ["ltdPlanDesignPercent", "ltdPlanDesignDollarAmount"],
  },
  ltdPlanDesignPercent: {
    label: "Percent",
    description: "LTD plan design percentage",
    cell: sharedCellSpecs.percentageAmount,
    nested: true,
    networkDependent: false,
    editableWhen: {
      dependsOn: "ltdPlanDesignType",
      disabledValues: ["notIncluded", "increments", NotDisclosed],
    },
  },
  ltdPlanDesignDollarAmount: {
    label: "Dollar Amount",
    description: "LTD plan design dollar amount",
    cell: sharedCellSpecs.dollarAmount,
    nested: true,
    networkDependent: false,
    editableWhen: {
      dependsOn: "ltdPlanDesignType",
      disabledValues: ["notIncluded", NotDisclosed],
    },
  },
  idiPlanDesignType: {
    label: "IDI Plan Design",
    description: "Type of IDI plan design",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosed(RfpIdiPlanDesignTypeEnum),
      },
    },
    networkDependent: false,
    refreshesCells: ["idiPlanDesignPercent", "idiPlanDesignDollarAmount"],
  },
  idiPlanDesignPercent: {
    label: "Percent",
    description: "IDI plan design percentage",
    cell: sharedCellSpecs.percentageAmount,
    nested: true,
    networkDependent: false,
    editableWhen: {
      dependsOn: "idiPlanDesignType",
      disabledValues: ["notIncluded", NotDisclosed],
    },
  },
  idiPlanDesignDollarAmount: {
    label: "Dollar Amount",
    description: "IDI plan design dollar amount",
    cell: sharedCellSpecs.dollarAmount,
    nested: true,
    networkDependent: false,
    editableWhen: {
      dependsOn: "idiPlanDesignType",
      disabledValues: ["notIncluded", "percentage", NotDisclosed],
    },
  },
  gsiBenefitMaximum: {
    label: "GSI Benefit Maximum",
    description: "Guaranteed Standard Issue benefit maximum amount",
    cell: sharedCellSpecs.dollarAmount,
    networkDependent: false,
  },
  ownOccupationPeriodType: {
    label: "Own Occupation Period",
    description: "Type of own occupation period",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: addNotDisclosed([
          "monthsOwnOccupation",
          "yearsOwnOccupation",
          "ownOccupationOnly",
          "notIncluded",
        ]),
      },
    },
    networkDependent: false,
    refreshesCells: ["ownOccupationPeriodAmount"],
  },
  ownOccupationPeriodAmount: {
    label: "Amount",
    description: "Own occupation period amount (years, months, or age)",
    cell: sharedCellSpecs.numberField,
    nested: true,
    networkDependent: false,
    defaultValue: 1,
    editableWhen: {
      dependsOn: "ownOccupationPeriodType",
      disabledValues: ["notIncluded", "ownOccupationOnly", NotDisclosed],
    },
  },
  definitionOfDisability: {
    label: "Definition of Disability",
    description: "How disability is defined for eligibility",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosed(DefinitionOfDisabilityEnum),
        formatValue: createEnumFormatter(definitionOfDisabilityLabels),
      },
      valueFormatter: createValueFormatter(definitionOfDisabilityLabels),
    },
    networkDependent: false,
  },
  eliminationPeriodDays: {
    label: "Elimination Period Days",
    description: "Number of days before benefits begin",
    cell: sharedCellSpecs.numberField,
    networkDependent: false,
  },
  ...buildTypeAmountBenefitPair({
    prefix: "benefitDuration",
    typeLabel: "Benefit Duration",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosed(BenefitDurationTypeEnum),
    typeCustomLabels: benefitDurationLabels,
    amountCell: sharedCellSpecs.numberField,
    typeDescription: "Type of benefit duration",
    amountDescription: "Duration amount (when applicable for type)",
    amountDefaultValue: 1,
    disabledValues: [
      ...Object.values(BenefitDurationTypeEnum).filter(
        (v) =>
          v !== BenefitDurationTypeEnum.lesserOfRbdOrNumberOfYears &&
          v !== BenefitDurationTypeEnum.yearDuration,
      ),
      NotDisclosed,
    ],
  }),
  ...buildPreExistingConditionBenefitPair({
    typeDescription: "Type of pre-existing condition limitation",
    monthsDetailDescription:
      "Configure months diagnosis free before/after effective date and treatment free. Format: XX/XX or XX/XX/XX",
    disabledValues: ["waived", "none", NotDisclosed],
    monthsDetailDefaultValue: "01/01",
  }),
  mentalIllnessBenefitType: {
    label: "Mental Illness Benefit",
    description: "Type of mental illness benefit",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: addNotDisclosed([
          "years",
          "months",
          "noLimit",
          "payableSameAsAnyOtherDisability",
          "included",
          "notIncluded",
        ]),
      },
    },
    networkDependent: false,
    refreshesCells: ["mentalIllnessBenefitTimeframe"],
  },
  mentalIllnessBenefitTimeframe: {
    label: "Timeframe",
    description: "Timeframe for mental illness benefit",
    cell: sharedCellSpecs.numberField,
    nested: true,
    networkDependent: false,
    editableWhen: {
      dependsOn: "mentalIllnessBenefitType",
      disabledValues: ["notIncluded", NotDisclosed],
    },
  },
  ...buildTypeAmountBenefitPair({
    prefix: "recoveryBenefit",
    typeLabel: "Recovery Benefit",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosed(RfpRecoveryBenefitEnum),
    amountCell: sharedCellSpecs.dollarAmount,
    typeDescription: "Type of recovery benefit included",
    amountDescription: "Recovery benefit amount",
    disabledValues: ["included", "notIncluded", NotDisclosed],
  }),
  ...buildTypeAmountBenefitPair({
    prefix: "catastrophicBenefit",
    typeLabel: "Catastrophic Benefit",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosed(RfpCatastrophicBenefitEnum),
    amountCell: sharedCellSpecs.wholePercentageAmount,
    typeDescription: "Type of catastrophic benefit included",
    amountDescription: "Catastrophic benefit amount",
    disabledValues: ["included", "notIncluded", NotDisclosed],
  }),
  portability: {
    label: "Portability",
    description: "Whether the policy is portable",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosed(RfpInclusionStatusEnum),
      },
    },
    networkDependent: false,
  },
  employerContribution: {
    label: "Employer Contribution",
    description: "Level of employer contribution",
    cell: {
      ...sharedCellSpecs.employerContribution,
      cellEditorParams: {
        ...sharedCellSpecs.employerContribution.cellEditorParams,
        values: extendEnumWithNotDisclosed(EmployerContributionEnum),
        formatValue: createEnumFormatter(employerContributionLabels),
      },
      valueFormatter: createValueFormatter(employerContributionLabels),
    },
    networkDependent: false,
  },
  participationRequirements: {
    label: "Participation Requirements",
    description: "Participation requirement details for the benefit plan",
    cell: sharedCellSpecs.text,
    networkDependent: false,
    benefitPlanLevel: true,
    requestedPlanRowVisibility: {
      field: "employerContribution",
      equals: EmployerContributionEnum.voluntary,
    },
  },
  ...buildTypeAmountBenefitPair({
    prefix: "rateGuarantee",
    typeLabel: "Rate Guarantee",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: Object.keys(RateGuaranteeTypeEnum),
    amountCell: sharedCellSpecs.numberField,
    typeDescription:
      "Time frame in which rates are guaranteed to stay the same",
    amountDescription: "Number of months or years the rate is guaranteed",
    amountDefaultValue: 1,
    disabledValues: [
      RateGuaranteeTypeEnum.notIncluded,
      RateGuaranteeTypeEnum.notOffered,
    ],
  }),
  enrolledLives: mpOnlyRow("Enrolled Lives", "enrolled_lives"),
} as const;
